import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const WinnerRoll = ({ giveaway, initialRollType, onClose, onRollComplete }) => {
  const navigate = useNavigate();
  const [rollType, setRollType] = useState(initialRollType || 'normal');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rolling, setRolling] = useState(false);
  const [winners, setWinners] = useState([]);
  const [winnerEntries, setWinnerEntries] = useState([]);
  const [winner, setWinner] = useState(null);
  const [winnerEntry, setWinnerEntry] = useState(null);
  const [showWheel, setShowWheel] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [rerollReason, setRerollReason] = useState('');
  const [error, setError] = useState('');
  const [showRerollOptions, setShowRerollOptions] = useState(false);
  const [wheelEntries, setWheelEntries] = useState([]); // Expanded entries for wheel display
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  const hasExistingRoll = giveaway.rollLogs && giveaway.rollLogs.length > 0;
  const hasInitiallyRolled = useRef(false);

  // Create weighted wheel entries from regular entries
  const createWheelEntries = useCallback((regularEntries) => {
    const expanded = [];
    regularEntries.forEach(entry => {
      const count = entry.entryCount || 1;
      for (let i = 0; i < count; i++) {
        expanded.push({
          ...entry,
          displayName: count > 1 ? `${entry.username} (${i + 1})` : entry.username,
          originalEntry: entry
        });
      }
    });
    return expanded;
  }, []);

  const drawWheel = useCallback((currentRotation) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = 240;
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2', '#F06292', '#AED581'];

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Save context
    ctx.save();

    // Move to center and rotate
    ctx.translate(centerX, centerY);
    ctx.rotate((currentRotation * Math.PI) / 180);
    ctx.translate(-centerX, -centerY);

    const segmentAngle = (2 * Math.PI) / wheelEntries.length;

    // Draw segments
    wheelEntries.forEach((entry, index) => {
      const startAngle = index * segmentAngle - Math.PI / 2;
      const endAngle = (index + 1) * segmentAngle - Math.PI / 2;
      const midAngle = (startAngle + endAngle) / 2;

      // Draw segment
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = colors[index % colors.length];
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Draw text
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(midAngle + Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${wheelEntries.length > 12 ? '14' : wheelEntries.length > 8 ? '16' : '18'}px Arial`;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
      ctx.shadowBlur = 3;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;
      
      const text = entry.displayName && entry.displayName.length > 15 
        ? entry.displayName.substring(0, 13) + '...' 
        : entry.displayName || entry.username;
      ctx.fillText(text, 0, -radius + 40);
      ctx.restore();
    });

    // Draw center circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, 50, 0, 2 * Math.PI);
    ctx.fillStyle = '#2d2d2d';
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 4;
    ctx.stroke();

    // Draw center emoji
    ctx.font = '40px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🎲', centerX, centerY);

    ctx.restore();
  }, [wheelEntries]);

  const fetchEntries = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/giveaways/${giveaway._id}/entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
      // Create weighted wheel entries
      setWheelEntries(createWheelEntries(response.data));
      return response.data;
    } catch (err) {
      console.error('Error fetching entries:', err);
      setError('Failed to load entries');
      return [];
    } finally {
      setLoading(false);
    }
  }, [giveaway._id, createWheelEntries]);

  const animateWheel = useCallback((targetRotation, duration) => {
    return new Promise((resolve) => {
      const startRotation = rotation;
      const startTime = Date.now();

      const animate = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function - ease out cubic
        const easeProgress = 1 - Math.pow(1 - progress, 3);

        const currentRotation = startRotation + (targetRotation - startRotation) * easeProgress;
        setRotation(currentRotation);

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      animate();
    });
  }, [rotation]);

  const handleRoll = useCallback(async (isReroll = false, entriesData = null, rollTypeOverride = null) => {
    const entriesToUse = entriesData || entries;
    const rollTypeToUse = rollTypeOverride || rollType;
    
    if (entriesToUse.length === 0) {
      setError('No eligible participants to roll');
      return;
    }

    setError('');
    setRolling(true);

    try {
      const token = localStorage.getItem('token');
      const endpoint = isReroll
        ? `/api/giveaways/${giveaway._id}/reroll`
        : `/api/giveaways/${giveaway._id}/roll`;

      const payload = { rollType: rollTypeToUse };
      if (isReroll && rerollReason) {
        payload.reason = rerollReason;
      }

      if (rollTypeToUse === 'spin_wheel') {
        // First, get the winner from the API
        const response = await axios.post(endpoint, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Show the wheel
        setShowWheel(true);
        setSpinning(true);
        
        // Wait for wheel to render
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Find the winner's index in the expanded wheelEntries array
        const winnerUserId = response.data.winner.userId || response.data.winner.userId;
        const wheelEntriesForSpin = createWheelEntries(entriesToUse);
        
        // Find a random occurrence of this winner in the wheel (since they may have multiple entries)
        const winnerIndices = wheelEntriesForSpin
          .map((e, idx) => ((e.user?._id || e.user) === winnerUserId ? idx : -1))
          .filter(idx => idx !== -1);
        
        if (winnerIndices.length > 0) {
          // Pick a random segment from the winner's entries
          const randomWinnerIndex = winnerIndices[Math.floor(Math.random() * winnerIndices.length)];
          
          // Calculate the target angle to land on the winner
          const segmentAngle = 360 / wheelEntriesForSpin.length;
          // Add 8 full rotations (2880 degrees) plus the winner's segment angle
          // The -90 offset aligns with the top pointer, and we add half segment to center
          const targetAngle = 2880 + (randomWinnerIndex * segmentAngle) + (segmentAngle / 2) - 90;
          
          // Animate the wheel spin
          await animateWheel(targetAngle, 4000);
        } else {
          // Fallback if winner not found
          await new Promise(resolve => setTimeout(resolve, 4000));
        }

        // Set winners data
        setWinners(response.data.winners || [response.data.winner]);
        setWinnerEntries(response.data.winnerEntries || [response.data.winnerEntry]);
        setWinner(response.data.winner);
        setWinnerEntry(response.data.winnerEntry);
        setSpinning(false);
      } else {
        // Direct roll - no animation
        const response = await axios.post(endpoint, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Set winners data
        setWinners(response.data.winners || [response.data.winner]);
        setWinnerEntries(response.data.winnerEntries || [response.data.winnerEntry]);
        setWinner(response.data.winner);
        setWinnerEntry(response.data.winnerEntry);
      }

      // Don't auto-close - let host review proofs and manually close
    } catch (err) {
      console.error('Roll error:', err);
      setError(err.response?.data?.message || 'Failed to roll winner');
      setSpinning(false);
      setShowWheel(false);
    } finally {
      setRolling(false);
    }
  }, [entries, rollType, rerollReason, giveaway._id, animateWheel]);

  useEffect(() => {
    fetchEntries();
    // If initialRollType is provided, start rolling immediately (only once)
    if (initialRollType && !hasInitiallyRolled.current) {
      hasInitiallyRolled.current = true;
      setTimeout(() => {
        fetchEntries().then((fetchedEntries) => {
          // Small delay to ensure entries are loaded
          setTimeout(() => handleRoll(hasExistingRoll, fetchedEntries, initialRollType), 500);
        });
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRollType]);

  useEffect(() => {
    // Draw wheel when entries are loaded and wheel should be shown
    if (showWheel && entries.length > 0 && canvasRef.current) {
      drawWheel(rotation);
    }
  }, [showWheel, entries, rotation, drawWheel]);

  const handleReroll = (type) => {
    setRollType(type);
    setWinner(null);
    setWinnerEntry(null);
    setShowWheel(false);
    setShowRerollOptions(false);
    setError('');
    setRotation(0); // Reset rotation
    // Cancel any ongoing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    // Pass the type directly to handleRoll to avoid state update delay
    setTimeout(() => handleRoll(true, null, type), 100);
  };

  const SpinWheel = () => {
    return (
      <div style={{ position: 'relative', width: '520px', height: '520px', margin: '30px auto' }}>
        {/* Pointer */}
        <div
          style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '20px solid transparent',
            borderRight: '20px solid transparent',
            borderTop: '40px solid #FF5722',
            zIndex: 10,
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
          }}
        />

        {/* Canvas */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '10px' }}>
          <canvas
            ref={canvasRef}
            id="wheelCanvas"
            width="500"
            height="500"
            style={{
              filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.3))'
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.85)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px',
        overflowY: 'auto'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '16px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '30px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#ffffff' }}>Loading participants...</p>
          </div>
        ) : winner ? (
          // Winner Announcement
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '5rem', marginBottom: '20px' }}>🎉</div>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '2rem', color: '#4CAF50' }}>
              {winners.length > 1 ? 'Winners Selected!' : 'Winner Selected!'}
            </h2>
            
            {/* Display all winners */}
            {winners.length > 1 ? (
              <div style={{ marginBottom: '20px' }}>
                {winners.map((w, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor: '#E8F5E9',
                      padding: '20px',
                      borderRadius: '12px',
                      marginBottom: '15px'
                    }}
                  >
                    <p style={{ margin: '0 0 10px 0', fontSize: '1rem', color: '#666' }}>
                      Winner #{w.position}
                    </p>
                    <p 
                      onClick={() => {
                        navigate(`/profile/${w.username}`);
                        onClose();
                      }}
                      style={{ 
                        margin: 0, 
                        fontSize: '2rem', 
                        fontWeight: 'bold', 
                        color: '#2E7D32',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#1B5E20';
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#2E7D32';
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                    >
                      @{w.username}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  backgroundColor: '#E8F5E9',
                  padding: '30px',
                  borderRadius: '12px',
                  marginBottom: '20px'
                }}
              >
                <p style={{ margin: '0 0 10px 0', fontSize: '1rem', color: '#666' }}>
                  The winner is...
                </p>
                <p 
                  onClick={() => {
                    navigate(`/profile/${winner.username}`);
                    onClose();
                  }}
                  style={{ 
                    margin: 0, 
                    fontSize: '2.5rem', 
                    fontWeight: 'bold', 
                    color: '#2E7D32',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#1B5E20';
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#2E7D32';
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  @{winner.username}
                </p>
                <p style={{ margin: '10px 0 0 0', fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>
                  Click to view profile
                </p>
              </div>
            )}

            {/* Winner's Proof Screenshots */}
            {winnerEntries.length > 0 && winnerEntries.some(we => we.proofs && we.proofs.length > 0) && (
              <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                {winnerEntries.map((we, winnerIndex) => (
                  we.proofs && we.proofs.length > 0 && (
                    <div key={winnerIndex} style={{ marginBottom: '20px' }}>
                      <h3 style={{ margin: '0 0 15px 0', fontSize: '1.2rem', color: '#ffffff' }}>
                        📸 {winnerEntries.length > 1 ? `Winner #${winnerIndex + 1}'s` : "Winner's"} Submitted Proofs
                      </h3>
                      <div style={{ display: 'grid', gap: '15px', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                        {we.proofs.map((proof, index) => (
                          <div
                            key={index}
                            style={{
                              backgroundColor: '#2d2d2d',
                              borderRadius: '8px',
                              padding: '10px',
                              border: '1px solid #3d3d3d'
                            }}
                          >
                            <p style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: '#999' }}>
                              Requirement {proof.requirementIndex + 1}
                            </p>
                            <a
                              href={proof.screenshot.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ display: 'block' }}
                            >
                              <img
                                src={proof.screenshot.url}
                                alt={`Proof ${index + 1}`}
                                style={{
                                  width: '100%',
                                  height: '150px',
                                  objectFit: 'cover',
                                  borderRadius: '6px',
                                  cursor: 'pointer'
                                }}
                              />
                            </a>
                            <p style={{ margin: '8px 0 0 0', fontSize: '0.75rem', color: '#64B5F6', textAlign: 'center' }}>
                              Click to view full size
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}

            <p style={{ color: '#b0b0b0', marginBottom: '20px' }}>
              Congratulations! 🎊 The host will contact you soon.
            </p>

            {/* Verification Link */}
            <div
              style={{
                backgroundColor: '#2d2d2d',
                padding: '15px',
                borderRadius: '12px',
                marginBottom: '20px',
                border: '1px solid #667eea'
              }}
            >
              <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', color: '#64B5F6' }}>
                🔗 Verification Link
              </h4>
              <p style={{ margin: '0 0 10px 0', fontSize: '0.85rem', color: '#b0b0b0' }}>
                Share this link to prove the giveaway results are legitimate:
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  value={`${window.location.origin}/verify?id=${giveaway.giveawayId}`}
                  readOnly
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #555',
                    fontSize: '0.9rem',
                    backgroundColor: '#1a1a1a',
                    color: '#ffffff'
                  }}
                />
                <button
                  onClick={() => {
                    const link = `${window.location.origin}/verify?id=${giveaway.giveawayId}`;
                    if (navigator.share) {
                      navigator.share({
                        title: `Verify Giveaway #${giveaway.giveawayId}`,
                        text: `Check the official verification for giveaway #${giveaway.giveawayId}`,
                        url: link
                      });
                    } else {
                      navigator.clipboard.writeText(link).then(() => {
                        alert('Verification link copied to clipboard!');
                      }).catch(() => {
                        const textArea = document.createElement('textarea');
                        textArea.value = link;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        alert('Verification link copied to clipboard!');
                      });
                    }
                  }}
                  style={{
                    padding: '10px 15px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 'bold'
                  }}
                >
                  📋 Copy Link
                </button>
              </div>
            </div>

            {/* Reroll Button */}
            {!showRerollOptions ? (
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <button
                  onClick={() => setShowRerollOptions(true)}
                  style={{
                    flex: 1,
                    padding: '15px',
                    backgroundColor: '#FF9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  🔄 Reroll Winner
                </button>
                <button
                  onClick={() => {
                    onRollComplete(); // Refresh the giveaway list
                    onClose(); // Close the modal
                  }}
                  style={{
                    flex: 1,
                    padding: '15px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  ✓ Confirm Winner
                </button>
              </div>
            ) : (
              <div
                style={{
                  backgroundColor: '#2d2d2d',
                  padding: '20px',
                  borderRadius: '12px',
                  marginBottom: '10px',
                  border: '1px solid #FF9800'
                }}
              >
                <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', color: '#FFA726' }}>
                  Reason for reroll (optional)
                </h4>
                <textarea
                  value={rerollReason}
                  onChange={(e) => setRerollReason(e.target.value)}
                  placeholder="e.g., Winner didn't respond, Winner was disqualified..."
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #555',
                    fontSize: '0.9rem',
                    resize: 'vertical',
                    backgroundColor: '#1a1a1a',
                    color: '#ffffff',
                    marginBottom: '15px'
                  }}
                />
                <h4 style={{ margin: '0 0 15px 0', fontSize: '1rem', color: '#FFA726' }}>
                  Choose Reroll Method:
                </h4>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <button
                    onClick={() => handleReroll('normal')}
                    disabled={rolling}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: rolling ? 'not-allowed' : 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: 'bold'
                    }}
                  >
                    ⚡ Direct Roll
                  </button>
                  <button
                    onClick={() => handleReroll('spin_wheel')}
                    disabled={rolling}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: rolling ? 'not-allowed' : 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: 'bold'
                    }}
                  >
                    🎡 Spin Wheel
                  </button>
                </div>
                <button
                  onClick={() => setShowRerollOptions(false)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#666',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 'bold'
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        ) : showWheel ? (
          // Spin Wheel Animation
          <div>
            <h2 style={{ textAlign: 'center', margin: '0 0 10px 0', color: '#ffffff' }}>🎡 Spinning the Wheel!</h2>
            <p style={{ textAlign: 'center', color: '#b0b0b0', marginBottom: '20px' }}>
              {spinning ? 'The wheel is spinning...' : 'Get ready!'}
            </p>
            <SpinWheel />
          </div>
        ) : (
          // Roll Selection
          <div>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '2rem', color: '#ffffff' }}>
              🎲 {hasExistingRoll ? 'Reroll Winner' : 'Roll for Winner'}
            </h2>

            {error && (
              <div
                style={{
                  backgroundColor: '#ffebee',
                  color: '#c62828',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '20px'
                }}
              >
                {error}
              </div>
            )}

            {/* Stats */}
            <div
              style={{
                backgroundColor: '#2d2d2d',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '20px',
                border: '1px solid #3d3d3d'
              }}
            >
              <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#999' }}>
                Total Eligible Participants
              </p>
              <p style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold', color: '#ffffff' }}>
                {entries.length}
              </p>
            </div>

            {/* Current Winner (if rerolling) */}
            {hasExistingRoll && giveaway.currentWinner && (
              <div
                style={{
                  backgroundColor: '#2d2d2d',
                  padding: '15px',
                  borderRadius: '12px',
                  marginBottom: '20px',
                  border: '1px solid #F57C00'
                }}
              >
                <p style={{ margin: '0 0 5px 0', fontSize: '0.9rem', color: '#FFA726' }}>
                  Current Winner
                </p>
                <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 'bold', color: '#ffffff' }}>
                  {giveaway.currentWinner.username}
                </p>
              </div>
            )}

            {/* Roll Type Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: '#ffffff' }}>
                Select Roll Method
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setRollType('normal')}
                  style={{
                    flex: 1,
                    padding: '15px',
                    backgroundColor: rollType === 'normal' ? '#2196F3' : '#2d2d2d',
                    color: rollType === 'normal' ? 'white' : '#b0b0b0',
                    border: '2px solid',
                    borderColor: rollType === 'normal' ? '#2196F3' : '#555',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    transition: 'all 0.3s'
                  }}
                >
                  ⚡ Normal Roll
                </button>
                <button
                  type="button"
                  onClick={() => setRollType('spin_wheel')}
                  style={{
                    flex: 1,
                    padding: '15px',
                    backgroundColor: rollType === 'spin_wheel' ? '#9C27B0' : '#2d2d2d',
                    color: rollType === 'spin_wheel' ? 'white' : '#b0b0b0',
                    border: '2px solid',
                    borderColor: rollType === 'spin_wheel' ? '#9C27B0' : '#555',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    transition: 'all 0.3s'
                  }}
                >
                  🎡 Spin Wheel
                </button>
              </div>
            </div>

            {/* Reroll Reason (if rerolling) */}
            {hasExistingRoll && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#ffffff' }}>
                  Reroll Reason (Optional)
                </label>
                <textarea
                  value={rerollReason}
                  onChange={(e) => setRerollReason(e.target.value)}
                  placeholder="e.g., Winner didn't respond, Winner was disqualified..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #555',
                    fontSize: '0.95rem',
                    resize: 'vertical',
                    backgroundColor: '#2d2d2d',
                    color: '#ffffff'
                  }}
                />
              </div>
            )}

            {/* Participants List */}
            <div style={{ marginBottom: '20px', maxHeight: '200px', overflowY: 'auto' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: '#ffffff' }}>
                Participants
              </label>
              <div
                style={{
                  backgroundColor: '#2d2d2d',
                  borderRadius: '8px',
                  padding: '10px',
                  border: '1px solid #3d3d3d'
                }}
              >
                {entries.map((entry, index) => (
                  <div
                    key={entry._id}
                    style={{
                      padding: '8px',
                      borderBottom: index < entries.length - 1 ? '1px solid #3d3d3d' : 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <span style={{ color: '#999', marginRight: '10px' }}>{index + 1}.</span>
                      <span style={{ fontWeight: 'bold', color: '#ffffff' }}>{entry.username}</span>
                    </div>
                    {entry.entryCount > 1 && (
                      <span
                        style={{
                          backgroundColor: '#667EEA',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {entry.entryCount} entries
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={onClose}
                disabled={rolling}
                style={{
                  flex: 1,
                  padding: '15px',
                  backgroundColor: '#9e9e9e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: rolling ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleRoll(hasExistingRoll)}
                disabled={rolling || entries.length === 0}
                style={{
                  flex: 2,
                  padding: '15px',
                  backgroundColor: rolling || entries.length === 0 ? '#ccc' : '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: rolling || entries.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '1.1rem',
                  fontWeight: 'bold'
                }}
              >
                {rolling ? '🎲 Rolling...' : hasExistingRoll ? '🔄 Reroll Winner' : '🎲 Roll Winner'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WinnerRoll;
